import React from 'react';
import { Box } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Typography } from '@monorepo/nova-ui';
import { Account, AccountBalance, Program } from '@monorepo/types';
import {
  DEFAULT_VALUES,
  OBJECTIVE_ICONS,
} from '../constants/objective-constants';
import { ObjectiveData } from '../types';
import {
  getCurrentInactiveDays,
  getInactivityLimit,
} from '../utils/objective-calculations';
import { BaseObjectiveProps, useObjectiveBase } from './base';

interface InactiveDaysObjectiveProps extends BaseObjectiveProps {
  account?: Account;
  accountBalance?: AccountBalance;
  program: Program | undefined | null;
}

export const InactiveDaysObjective = ({
  accountBalance,
  program,
}: InactiveDaysObjectiveProps): ObjectiveData => {
  const { t, theme, valueProps, getMainTextStyle, getSecondaryTextStyle } =
    useObjectiveBase();

  const currentInactiveDays = getCurrentInactiveDays(accountBalance);
  const inactivityLimit = getInactivityLimit(program);

  const primaryLabel = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{ color: theme.palette.text.second }}
      >
        {t('objectives.inactiveDays.label')}
      </Typography>
    </Box>
  );

  const inactiveDaysData = (
    <React.Fragment>
      <Typography variant="subtitle1" sx={getMainTextStyle} {...valueProps}>
        {t('objectives.inactiveDays.current', {
          current: currentInactiveDays,
        })}
      </Typography>
      <Typography variant="body2" sx={getSecondaryTextStyle}>
        {t('objectives.inactiveDays.limit', {
          limit: inactivityLimit,
        })}
      </Typography>
    </React.Fragment>
  );

  return {
    icon: (
      <SystemIcon
        iconName={OBJECTIVE_ICONS.WARNING_CIRCLE}
        size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
      />
    ),
    primaryText: primaryLabel,
    secondaryText: inactiveDaysData,
  };
};
